/**
 * Estado del carrito: las líneas, su persistencia y las acciones.
 *
 * La matemática NO vive acá — está en `lib/carrito.js`, pura y probada sin
 * navegador. Este hook sólo sostiene el estado y lo escribe en `localStorage`.
 *
 * Las acciones usan la forma funcional de `setState` (`setLineas(prev => …)`) a
 * propósito. El 2026-09-02 el reorden del panel perdía movimientos porque leía el
 * estado por closure: dos eventos en el mismo render trabajaban sobre la misma
 * copia vieja y uno se perdía. Tocar "+" tres veces rápido es exactamente ese
 * patrón, y acá no puede pasar.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import * as carrito from './carrito';

export function useCarrito(productos, ajustes) {
  const [lineas, setLineas] = useState(() => carrito.leer());
  const [abierto, setAbierto] = useState(false);

  useEffect(() => {
    carrito.guardar(lineas);
  }, [lineas]);

  const agregar = useCallback((id, cantidad = 1) => {
    setLineas((prev) => carrito.agregar(prev, id, cantidad));
  }, []);
  const quitar = useCallback((id) => {
    setLineas((prev) => carrito.quitar(prev, id));
  }, []);
  const cambiarCantidad = useCallback((id, cantidad) => {
    setLineas((prev) => carrito.cambiarCantidad(prev, id, cantidad));
  }, []);
  const vaciar = useCallback(() => setLineas(carrito.vaciar()), []);

  // El resumen se recalcula cuando cambia el carrito, el catálogo o los ajustes.
  // Que dependa del catálogo es lo que hace que un cambio de precio de la dueña
  // se refleje en un carrito armado ayer.
  const resumen = useMemo(
    () => carrito.calcular(lineas, productos, ajustes),
    [lineas, productos, ajustes]
  );

  const mensaje = useMemo(() => carrito.mensajeWhatsApp(resumen), [resumen]);

  /** Ids que están en el carrito, para que la card sepa mostrar "Agregado". */
  const enCarrito = useMemo(() => new Set(lineas.map((l) => l.id)), [lineas]);

  return {
    lineas, resumen, mensaje, enCarrito,
    agregar, quitar, cambiarCantidad, vaciar,
    abierto, setAbierto,
    lleno: lineas.length >= carrito.LIMITE_PRODUCTOS,
  };
}
